import React, { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";
import { Button, styled, Box, padding } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useLazyQuery } from "@apollo/client";
import { GET_GSC_SITES } from "../../gql/queries/gsc";
import { GET_GA_PROPERTIES } from "../../gql/queries/ga4";
import { GET_COMBINED_DASHBOARD } from "../../gql/queries/combinedAnalytics";

const COLORS = ["#4285f4", "#34a853", "#fbbc04", "#ea4335", "#9c27b0", "#00bcd4"];

const CombinedInsightsWrapper = styled(Box)(({ theme }) => {
  const border = alpha(theme.palette.divider, 0.8);

  return {
    // Page wrapper
    width: "100%",
    padding: 0,
    borderRadius: theme.shape.borderRadius * 2,
    background: theme.palette.background.default,
    color: theme.palette.text.primary,

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },

    // ---------- Header ----------
    "& .insights-header": {
      marginTop: "20px",
      marginBottom: theme.spacing(2.5),
      padding: theme.spacing(2.5),
      borderRadius: theme.shape.borderRadius * 2,
      // background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.04)})`,
      background: theme.palette.primary.main,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
    },
    "& .insights-header h2": {
      margin: 0,
      fontWeight: 800,
      letterSpacing: "-0.02em",
      fontSize: theme.typography.h5.fontSize,
      lineHeight: 1.2,
      color: "white",
    },
    "& .header-subtitle": {
      margin: theme.spacing(0.75, 0, 0),
      color: "white",
      fontSize: theme.typography.body2.fontSize,
    },

    // ---------- Cards ----------
    "& .property-selection-card, & .error-card, & .selected-summary, & .tab-content, & .initial-state, & .health-score-card, & .health-metrics, & .health-recommendations, & .trend-chart, & .device-chart, & .geo-opportunities, & .content-section, & .overall-metrics, & .opportunity-category":
      {
        borderRadius: theme.shape.borderRadius * 2,
        border: `1px solid ${border}`,
        background: theme.palette.background.paper,
      },

    "& .opportunity-category": {
      marginTop: "15px",
    },

    "& .property-selection-card": {
      padding: theme.spacing(2.5),
      boxShadow: theme.shadows[1],
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      // marginBottom: theme.spacing(2),
    },
    "& .property-selection-card h3": {
      margin: 0,
      fontSize: theme.typography.h6.fontSize,
      fontWeight: 800,
      letterSpacing: "-0.01em",
    },
    "& .selection-note": {
      margin: theme.spacing(1, 0, 2),
      padding: theme.spacing(1.25, 1.5),
      borderRadius: theme.shape.borderRadius * 1.5,
      background: alpha(theme.palette.warning.main, 0.1),
      border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
      color: theme.palette.text.primary,
      fontSize: theme.typography.body2.fontSize,
    },

    // ---------- Selection grid ----------
    "& .selection-grid": {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: theme.spacing(2),
      marginTop: theme.spacing(1),
      [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
      },
    },
    "& .selection-group": {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(0.75),
    },
    "& .selection-label": {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      fontWeight: 700,
      color: theme.palette.text.primary,
      fontSize: theme.typography.body2.fontSize,
    },
    "& .label-icon": {
      display: "inline-flex",
      width: 28,
      height: 28,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 8,
      background: alpha(theme.palette.text.primary, 0.06),
      border: `1px solid ${border}`,
      fontSize: 14,
    },

    "& .selection-loading": {
      padding: theme.spacing(1.5),
      borderRadius: theme.shape.borderRadius * 1.5,
      border: `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
      color: theme.palette.text.secondary,
      background: alpha(theme.palette.background.default, 0.6),
      fontSize: theme.typography.body2.fontSize,
    },

    // ---------- Select inputs ----------
    "& .property-select, & .date-select": {
      appearance: "none",
      WebkitAppearance: "none",
      MozAppearance: "none",
      width: "100%",
      padding: theme.spacing(1.25, 1.5),
      borderRadius: theme.shape.borderRadius * 1.5,
      border: `1px solid ${border}`,
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
      outline: "none",
      fontSize: theme.typography.body2.fontSize,
      transition: "border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease",
      boxShadow: "none",
      backgroundImage: "linear-gradient(45deg, transparent 50%, currentColor 50%), linear-gradient(135deg, currentColor 50%, transparent 50%)",
      backgroundPosition: "calc(100% - 18px) calc(50% - 2px), calc(100% - 12px) calc(50% - 2px)",
      backgroundSize: "6px 6px, 6px 6px",
      backgroundRepeat: "no-repeat",
    },
    "& .property-select:focus, & .date-select:focus": {
      borderColor: alpha(theme.palette.primary.main, 0.65),
      boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.15)}`,
    },

    // ---------- Match status banners ----------
    "& .match-warning, & .match-success": {
      marginTop: theme.spacing(1.5),
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 1.5),
      borderRadius: theme.shape.borderRadius * 1.5,
      fontSize: theme.typography.body2.fontSize,
      fontWeight: 600,
    },
    "& .match-warning": {
      background: alpha(theme.palette.warning.main, 0.1),
      border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
    },
    "& .match-success": {
      background: alpha(theme.palette.success.main, 0.1),
      border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
    },

    // ---------- Controls row ----------
    "& .analysis-controls": {
      marginTop: theme.spacing(2),
      display: "flex",
      gap: theme.spacing(1.5),
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
    },
    "& .date-select": {
      maxWidth: 220,
      [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },

    // ---------- Buttons (your classnames) ----------
    "& .btn": {
      textTransform: "none",
      borderRadius: theme.shape.borderRadius * 1.5,
      padding: theme.spacing(1.2, 1.6),
      fontWeight: 800,
      letterSpacing: "-0.01em",
      boxShadow: "none",
      border: `1px solid ${border}`,
      transition: "transform 120ms ease, box-shadow 120ms ease, background 120ms ease",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: theme.shadows[2],
      },
      "&:active": {
        transform: "translateY(0px)",
      },
      "&.Mui-disabled": {
        opacity: 0.55,
        cursor: "not-allowed",
      },
    },
    "& .btn-primary": {
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderColor: alpha(theme.palette.primary.main, 0.35),
      "&:hover": {
        background: theme.palette.primary.dark,
      },
    },
    "& .btn-secondary": {
      background: alpha(theme.palette.text.primary, 0.04),
      color: theme.palette.text.primary,
      "&:hover": {
        background: alpha(theme.palette.text.primary, 0.07),
      },
    },
    "& .btn-analyze": {
      minWidth: 220,
      [theme.breakpoints.down("sm")]: { width: "100%", minWidth: "unset" },
    },

    // Spinner for "Analyzing..."
    "@keyframes ciSpin": {
      from: { transform: "rotate(0deg)" },
      to: { transform: "rotate(360deg)" },
    },
    "& .btn-spinner": {
      width: 16,
      height: 16,
      display: "inline-block",
      borderRadius: "50%",
      border: `2px solid ${alpha(theme.palette.primary.contrastText, 0.35)}`,
      borderTopColor: theme.palette.primary.contrastText,
      marginRight: theme.spacing(1),
      animation: "ciSpin 700ms linear infinite",
      verticalAlign: "middle",
    },

    // ---------- Error card ----------
    "& .error-card": {
      padding: theme.spacing(2),
      marginTop: theme.spacing(1.5),
      borderColor: alpha(theme.palette.error.main, 0.3),
      background: alpha(theme.palette.error.main, 0.06),
      "& p": {
        margin: 0,
        fontWeight: 700,
      },
      "& .btn-secondary": {
        marginTop: theme.spacing(1.25),
      },
    },

    // ---------- Selected summary ----------
    "& .selected-summary": {
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(1.5),
      padding: theme.spacing(1.5),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(1.25),
      flexWrap: "wrap",
    },
    "& .summary-item": {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(0.75, 1.2),
      borderRadius: 999,
      border: `1px solid ${border}`,
      background: alpha(theme.palette.background.default, 0.7),
    },
    "& .summary-label": {
      fontWeight: 800,
      color: theme.palette.text.secondary,
      fontSize: theme.typography.caption.fontSize,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    },
    "& .summary-value": {
      fontWeight: 800,
      fontSize: theme.typography.body2.fontSize,
    },
    "& .summary-connector": {
      fontWeight: 900,
      color: theme.palette.text.secondary,
    },

    // ---------- Tabs ----------
    "& .tab-navigation": {
      display: "flex",
      gap: theme.spacing(1),
      flexWrap: "wrap",
      padding: theme.spacing(1.25),
      borderRadius: theme.shape.borderRadius * 2,
      border: `1px solid ${border}`,
      background: alpha(theme.palette.background.paper, 0.8),
      marginBottom: theme.spacing(1.5),
    },
    "& .tab-btn": {
      textTransform: "none",
      borderRadius: 999,
      padding: theme.spacing(1, 1.5),
      border: `1px solid ${border}`,
      background: alpha(theme.palette.text.primary, 0.03),
      fontWeight: 800,
      color: theme.palette.text.primary,
      boxShadow: "none",
      "&:hover": {
        background: alpha(theme.palette.text.primary, 0.06),
      },
    },
    "& .tab-btn.active": {
      background: alpha(theme.palette.primary.main, 0.12),
      borderColor: alpha(theme.palette.primary.main, 0.35),
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
    },

    "& .tab-content": {
      padding: theme.spacing(2.5),
      boxShadow: theme.shadows[1],
      [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },

    // ---------- Initial state ----------
    "& .initial-state": {
      marginTop: theme.spacing(2),
      padding: theme.spacing(3),
      textAlign: "center",
      boxShadow: theme.shadows[1],
    },
    "& .initial-icon": {
      width: 52,
      height: 52,
      margin: "0 auto",
      display: "grid",
      placeItems: "center",
      borderRadius: 16,
      fontSize: 26,
      background: alpha(theme.palette.primary.main, 0.12),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
    },
    "& .initial-state h3": {
      margin: theme.spacing(1.5, 0, 0.5),
      fontWeight: 600,
      letterSpacing: "-0.02em",
    },
    "& .initial-state p": {
      margin: 0,
      color: theme.palette.text.secondary,
    },
    "& .benefits-list": {
      marginTop: theme.spacing(2),
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: theme.spacing(1.25),
      [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    "& .benefit-item": {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 1.5),
      borderRadius: theme.shape.borderRadius * 2,
      border: `1px solid ${border}`,
      background: alpha(theme.palette.background.default, 0.7),
      fontWeight: 600,
      justifyContent: "center",
    },
    "& .benefit-icon": {
      fontSize: 18,
    },

    // ---------- Tables ----------
    "& .table-container": {
      marginTop: theme.spacing(2),
      overflow: "auto",
      borderRadius: theme.shape.borderRadius * 2,
      border: `1px solid ${border}`,
      marginBottom: "12px",
    },
    "& .data-table": {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      minWidth: 900,
      background: theme.palette.background.paper,
    },
    "& .data-table thead th": {
      position: "sticky",
      top: 0,
      zIndex: 1,
      textAlign: "left",
      padding: theme.spacing(1.2, 1.25),
      fontSize: theme.typography.caption.fontSize,
      fontWeight: 900,
      color: theme.palette.text.secondary,
      background: alpha(theme.palette.background.default, 0.85),
      borderBottom: `1px solid ${border}`,
      backdropFilter: "blur(8px)",
      whiteSpace: "nowrap",
    },
    "& .data-table tbody td": {
      padding: theme.spacing(1.2, 1.25),
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
      fontSize: theme.typography.body2.fontSize,
      whiteSpace: "nowrap",
    },
    "& .data-table tbody tr:hover td": {
      background: alpha(theme.palette.primary.main, 0.05),
    },
    "& .page-cell, & .keyword-cell": {
      maxWidth: 360,
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    "& .gsc-col": {
      background: alpha(theme.palette.info.main, 0.05),
    },
    "& .ga-col": {
      background: alpha(theme.palette.success.main, 0.05),
    },

    // ---------- Badges ----------
    "& .opportunity-badge, & .potential-badge, & .priority-badge": {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 999,
      padding: theme.spacing(0.4, 1),
      fontSize: theme.typography.caption.fontSize,
      fontWeight: 900,
      border: `1px solid ${border}`,
      background: alpha(theme.palette.text.primary, 0.04),
      textTransform: "capitalize",
      whiteSpace: "nowrap",
    },
    "& .opportunity-badge.high, & .potential-badge.high": {
      background: alpha(theme.palette.success.main, 0.12),
      borderColor: alpha(theme.palette.success.main, 0.25),
    },
    "& .opportunity-badge.medium, & .potential-badge.medium": {
      background: alpha(theme.palette.warning.main, 0.12),
      borderColor: alpha(theme.palette.warning.main, 0.25),
    },
    "& .opportunity-badge.low, & .potential-badge.low": {
      background: alpha(theme.palette.error.main, 0.1),
      borderColor: alpha(theme.palette.error.main, 0.22),
    },
    "& .priority-badge.high": {
      background: alpha(theme.palette.error.main, 0.1),
      borderColor: alpha(theme.palette.error.main, 0.22),
    },
    "& .priority-badge.medium": {
      background: alpha(theme.palette.warning.main, 0.12),
      borderColor: alpha(theme.palette.warning.main, 0.25),
    },
    "& .priority-badge.low": {
      background: alpha(theme.palette.success.main, 0.12),
      borderColor: alpha(theme.palette.success.main, 0.25),
    },
    "& .opportunity-items": {
      // marginBottom: "15px",
      paddingLeft: "10px",
    },
    "& .category-header": {
      marginBottom: "10px",
      marginTop: "10px",
    },
    "& .category-header h4": {
      paddingLeft: "6px",
    },
    "& .category-header p": {
      paddingLeft: "10px",
    },
    "& .category-header span": {
      paddingLeft: "10px",
    },
    "& .opportunity-item.priority-low, & .opportunity-item.priority-high, & .opportunity-item.priority-medium": {
      paddingBottom: "15px",
    },

    // ---------- SEO Health view ----------
    "& .seo-health-view h3, & .trend-correlation-view h3, & .device-comparison-view h3, & .geo-performance-view h3, & .content-gaps-view h3, & .funnel-view h3, & .landing-pages-view h3, & .keywords-view h3, & .opportunities-view h3":
      {
        margin: 0,
        fontWeight: 900,
        letterSpacing: "-0.02em",
      },

    "& .content-gaps-view h3": {
      paddingBottom: "10px",
    },

    "& .health-score-card": {
      marginTop: theme.spacing(2),
      padding: theme.spacing(2),
      display: "grid",
      gridTemplateColumns: "220px 1fr",
      gap: theme.spacing(2),
      alignItems: "center",
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    "& .score-circle": {
      width: 200,
      height: 200,
      borderRadius: "50%",
      border: `8px solid ${alpha(theme.palette.primary.main, 0.35)}`,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto",
      background: alpha(theme.palette.background.default, 0.6),
    },
    "& .score-grade": {
      fontWeight: 1000,
      fontSize: 40,
      lineHeight: 1,
    },
    "& .score-number": {
      marginTop: theme.spacing(0.5),
      fontWeight: 800,
      color: theme.palette.text.secondary,
    },

    "& .score-comparison": {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: theme.spacing(1.25),
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    "& .comparison-item": {
      padding: theme.spacing(1.25, 1.5),
      borderRadius: theme.shape.borderRadius * 2,
      border: `1px solid ${border}`,
      background: alpha(theme.palette.background.default, 0.7),
    },
    "& .comparison-label": {
      display: "block",
      color: theme.palette.text.secondary,
      fontSize: theme.typography.caption.fontSize,
      fontWeight: 800,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      marginBottom: theme.spacing(0.5),
    },
    "& .comparison-value": {
      fontWeight: 1000,
      fontSize: theme.typography.h6.fontSize,
    },
    "& .comparison-value.positive": { color: theme.palette.success.main },
    "& .comparison-value.negative": { color: theme.palette.error.main },

    "& .health-metrics": {
      marginTop: theme.spacing(2),
      padding: theme.spacing(2),
    },
    "& .health-metrics h4": {
      margin: 0,
      fontWeight: 900,
      color: theme.palette.text.primary,
    },
    "& .metrics-list": {
      marginTop: theme.spacing(1.5),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
    "& .metric-row": {
      display: "grid",
      gridTemplateColumns: "1fr 1.5fr",
      gap: theme.spacing(2),
      alignItems: "center",
      padding: theme.spacing(1.1, 1.25),
      borderRadius: theme.shape.borderRadius * 2,
      border: `1px solid ${border}`,
      background: alpha(theme.palette.background.default, 0.6),
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    "& .metric-info": {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      fontWeight: 900,
    },
    "& .metric-icon": {
      width: 26,
      height: 26,
      borderRadius: 8,
      display: "grid",
      placeItems: "center",
      background: alpha(theme.palette.text.primary, 0.06),
      border: `1px solid ${border}`,
    },
    "& .metric-values": {
      display: "grid",
      gridTemplateColumns: "120px 1fr 56px",
      gap: theme.spacing(1.25),
      alignItems: "center",
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    "& .metric-actual": {
      fontWeight: 900,
      color: theme.palette.text.primary,
    },
    "& .metric-score": {
      textAlign: "right",
      fontWeight: 1000,
      color: theme.palette.text.secondary,
      [theme.breakpoints.down("sm")]: { textAlign: "left" },
    },
    "& .score-bar": {
      height: 10,
      borderRadius: 999,
      overflow: "hidden",
      border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
      background: alpha(theme.palette.text.primary, 0.06),
    },
    "& .score-fill": {
      height: "100%",
      borderRadius: 999,
    },

    "& .health-recommendations": {
      marginTop: theme.spacing(2),
      padding: theme.spacing(2),
    },
    "& .health-recommendations h4": {
      margin: 0,
      fontWeight: 900,
    },
    "& .recommendation-item": {
      marginTop: theme.spacing(1),
      padding: theme.spacing(1.25, 1.5),
      borderRadius: theme.shape.borderRadius * 2,
      border: `1px solid ${border}`,
      background: alpha(theme.palette.background.default, 0.6),
      display: "grid",
      gridTemplateColumns: "140px 1fr 90px",
      gap: theme.spacing(1),
      alignItems: "start",
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    "& .rec-area": {
      fontWeight: 1000,
    },
    "& .rec-message": {
      color: theme.palette.text.secondary,
      fontWeight: 700,
    },
    "& .rec-priority": {
      justifySelf: "end",
      borderRadius: 999,
      padding: theme.spacing(0.35, 1),
      fontWeight: 1000,
      textTransform: "capitalize",
      border: `1px solid ${border}`,
      [theme.breakpoints.down("sm")]: { justifySelf: "start" },
    },
    "& .rec-priority.high": {
      background: alpha(theme.palette.error.main, 0.1),
      borderColor: alpha(theme.palette.error.main, 0.22),
      fontSize: "15px",
    },
    "& .rec-priority.medium": {
      background: alpha(theme.palette.warning.main, 0.12),
      borderColor: alpha(theme.palette.warning.main, 0.25),
      fontSize: "15px",
    },
    "& .rec-priority.low": {
      background: alpha(theme.palette.success.main, 0.12),
      borderColor: alpha(theme.palette.success.main, 0.25),
      fontSize: "15px",
    },

    // ---------- Charts containers ----------
    "& .trend-chart, & .device-chart": {
      marginTop: theme.spacing(2),
      padding: theme.spacing(2),
      marginBottom: "10px",
    },

    // ---------- Content Gaps-----------
    "& .stat-values": {
      paddingBottom: "8px",
    },
    "& .content-section": {
      padding: "10px",
    },

    // -----------Geography-----------
    "& .opportunity-section": {
      padding: "10px",
    },

    // ---------- Funnel ----------
    "& .funnel-stages": {
      marginTop: theme.spacing(2),
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: theme.spacing(1.5),
      [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    "& .funnel-stage": {
      padding: theme.spacing(1.75),
      borderRadius: theme.shape.borderRadius * 2,
      border: `1px solid ${border}`,
      background: alpha(theme.palette.background.default, 0.7),
    },
    "& .stage-header": {
      display: "flex",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      alignItems: "baseline",
    },
    "& .stage-name": { fontWeight: 1000 },
    "& .stage-source": { color: theme.palette.text.secondary, fontSize: theme.typography.caption.fontSize, fontWeight: 800 },
    "& .stage-value": { marginTop: theme.spacing(1), fontSize: theme.typography.h5.fontSize, fontWeight: 1000 },
    "& .stage-description": { marginTop: theme.spacing(0.5), color: theme.palette.text.secondary, fontWeight: 700 },
    "& .stage-conversion": { marginTop: theme.spacing(1), fontWeight: 900, color: theme.palette.primary.main },

    "& .overall-metrics": {
      marginTop: theme.spacing(2),
      padding: theme.spacing(2),
    },
    "& .metrics-grid": {
      marginTop: theme.spacing(1.5),
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: theme.spacing(1.25),
      [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    "& .metric-card": {
      padding: theme.spacing(1.5),
      borderRadius: theme.shape.borderRadius * 2,
      border: `1px solid ${border}`,
      background: alpha(theme.palette.background.default, 0.7),
    },
    "& .metric-card.highlight": {
      borderColor: alpha(theme.palette.primary.main, 0.35),
      background: alpha(theme.palette.primary.main, 0.1),
    },
    "& .metric-label": {
      display: "block",
      color: theme.palette.text.secondary,
      fontSize: theme.typography.caption.fontSize,
      fontWeight: 900,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    },
    "& .metric-value": {
      marginTop: theme.spacing(0.5),
      fontWeight: 1100,
      fontSize: theme.typography.h6.fontSize,
    },
  };
});

function CombinedInsights({ gscEmail, gaEmail }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("health");
  const [dateRange, setDateRange] = useState(30);

  const [gscSites, setGscSites] = useState([]);
  const [gaProperties, setGaProperties] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [selectedProperty, setSelectedProperty] = useState("");
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [analysisStarted, setAnalysisStarted] = useState(false);

  const [loadGscSites] = useLazyQuery(GET_GSC_SITES, {
    fetchPolicy: "network-only",
  });

  const [loadGaProperties] = useLazyQuery(GET_GA_PROPERTIES, {
    fetchPolicy: "network-only",
  });

  // const [loadCombinedData] = useLazyQuery(GET_COMBINED_LANDING_PAGES, {
  //   fetchPolicy: "network-only",
  // });

  const [loadCombinedDashboard] = useLazyQuery(GET_COMBINED_DASHBOARD, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (gscEmail) fetchGscSites();
  }, [gscEmail]);

  useEffect(() => {
    if (gaEmail) fetchGaProperties();
  }, [gaEmail]);

  // const fetchGscSites = async () => {
  //   setLoadingSites(true);
  //   try {
  //     const response = await fetch(`http://localhost:8000/api/auth/gsc/sites?email=${encodeURIComponent(gscEmail)}`);
  //     const data = await response.json();
  //     if (data.success && data.sites) setGscSites(data.sites);
  //   } catch (err) {
  //     console.error("Error fetching GSC sites:", err);
  //   } finally {
  //     setLoadingSites(false);
  //   }
  // };

  const fetchGscSites = async () => {
    setLoadingSites(true);
    try {
      const { data } = await loadGscSites({
        variables: {
          email: gscEmail,
        },
      });

      if (data?.gscSites?.success && data.gscSites.sites) {
        setGscSites(data.gscSites.sites);
      }
    } catch (err) {
      console.error("Error fetching GSC sites:", err);
    } finally {
      setLoadingSites(false);
    }
  };

  // const fetchGaProperties = async () => {
  //   setLoadingProperties(true);
  //   try {
  //     const response = await fetch(`http://localhost:8000/api/ga/properties?email=${encodeURIComponent(gaEmail)}`);
  //     const data = await response.json();
  //     if (data.success && data.properties) setGaProperties(data.properties);
  //   } catch (err) {
  //     console.error("Error fetching GA properties:", err);
  //   } finally {
  //     setLoadingProperties(false);
  //   }
  // };

  const fetchGaProperties = async () => {
    setLoadingProperties(true);
    try {
      const { data } = await loadGaProperties({
        variables: {
          email: gaEmail,
          account_id: null, // optional, matches backend
        },
      });

      if (data?.gaProperties?.success && data.gaProperties.properties) {
        setGaProperties(data.gaProperties.properties);
      }
    } catch (err) {
      console.error("Error fetching GA properties:", err);
    } finally {
      setLoadingProperties(false);
    }
  };

  // const fetchCombinedData = async () => {
  //   if (!selectedSite || !selectedProperty) {
  //     setError("Please select both a GSC site and GA4 property");
  //     return;
  //   }

  //   setLoading(true);
  //   setError(null);
  //   setAnalysisStarted(true);

  //   try {
  //     const endDate = new Date().toISOString().split("T")[0];
  //     const startDate = new Date(Date.now() - dateRange * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

  //     const response = await fetch(
  //       `http://localhost:8000/api/combined/dashboard?site_url=${encodeURIComponent(
  //         selectedSite,
  //       )}&property_id=${selectedProperty}&start_date=${startDate}&end_date=${endDate}&email=${encodeURIComponent(gscEmail || "")}`,
  //     );

  //     const result = await response.json();
  //     if (result.success) {
  //       setInfo(result.data);
  //     } else {
  //       setError(result.detail || "Failed to fetch combined data");
  //     }
  //   } catch (err) {
  //     console.error("Error fetching combined data:", err);
  //     setError("Failed to fetch combined analytics data");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchCombinedData = async () => {
    if (!selectedSite || !selectedProperty) {
      setError("Please select both a GSC site and GA4 property");
      return;
    }

    setLoading(true);
    setError(null);
    setAnalysisStarted(true);

    try {
      const endDate = new Date().toISOString().split("T")[0];
      const startDate = new Date(Date.now() - dateRange * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

      const { data } = await loadCombinedDashboard({
        variables: {
          site_url: selectedSite,
          property_id: selectedProperty,
          start_date: startDate,
          end_date: endDate,
          email: gscEmail || null,
        },
      });

      const result = data?.combinedDashboard;
      if (result?.success) {
        setInfo(result.data);
      } else {
        setError("Failed to fetch combined analytics data");
      }
    } catch (err) {
      console.error("Error fetching combined data:", err);
      setError("Failed to fetch combined analytics data");
    } finally {
      setLoading(false);
    }
  };

  const extractDomain = url => {
    try {
      if (url.startsWith("sc-domain:")) return url.replace("sc-domain:", "").toLowerCase();
      const parsed = new URL(url);
      return parsed.hostname.replace("www.", "").toLowerCase();
    } catch {
      return url.toLowerCase();
    }
  };

  const checkPropertyMatch = () => {
    if (!selectedSite || !selectedProperty) return null;
    const gscDomain = extractDomain(selectedSite);
    const selectedGaProp = gaProperties.find(p => p.propertyId === selectedProperty);
    if (!selectedGaProp) return null;
    const gaName = selectedGaProp.displayName?.toLowerCase() || "";
    if (gaName.includes(gscDomain) || gscDomain.includes(gaName.replace(/[^a-z0-9]/g, ""))) return "match";
    return "warning";
  };

  const matchStatus = checkPropertyMatch();

  return (
    // <Box className="combined-insights">
    <CombinedInsightsWrapper>
      <Box className="insights-header">
        <h2>🔗 Combined GSC + GA4 Insights</h2>
        <p className="header-subtitle">Deep analysis combining search performance with user behavior</p>
      </Box>

      {/* Property Selection */}
      <Box className="property-selection-card">
        <h3>Select Properties to Analyze</h3>
        <p className="selection-note">
          ⚠️ Select the GSC site and GA4 property for the <strong>same website</strong>.
        </p>

        <Box className="selection-grid">
          <Box className="selection-group">
            <label htmlFor="google-console" className="selection-label">
              <span className="label-icon gsc-icon">📊</span>
              Google Search Console Site
            </label>
            {loadingSites ? (
              <Box className="selection-loading">Loading sites...</Box>
            ) : (
              <select
                value={selectedSite}
                onChange={e => {
                  setSelectedSite(e.target.value);
                  setAnalysisStarted(false);
                  setInfo(null);
                }}
                className="property-select gsc-select"
              >
                <option value="">-- Select GSC Site --</option>
                {gscSites.map((site, index) => (
                  <option value={site.siteUrl}>{site.displayUrl || site.siteUrl}</option>
                ))}
              </select>
            )}
          </Box>

          <Box className="selection-group">
            <label htmlFor="google-analytics" className="selection-label">
              <span className="label-icon ga-icon">📈</span>
              Google Analytics 4 Property
            </label>
            {loadingProperties ? (
              <Box className="selection-loading">Loading properties...</Box>
            ) : (
              <select
                value={selectedProperty}
                onChange={e => {
                  setSelectedProperty(e.target.value);
                  setAnalysisStarted(false);
                  setInfo(null);
                }}
                className="property-select ga-select"
              >
                <option value="">-- Select GA4 Property --</option>
                {gaProperties.map(prop => (
                  <option key={prop.propertyId} value={prop.propertyId}>
                    {prop.displayName} ({prop.propertyId})
                  </option>
                ))}
              </select>
            )}
          </Box>
        </Box>

        {selectedSite && selectedProperty && matchStatus === "warning" && (
          <Box className="match-warning">
            <span className="warning-icon">⚠️</span>
            <span>Properties may not match. Verify they belong to the same domain.</span>
          </Box>
        )}

        {selectedSite && selectedProperty && matchStatus === "match" && (
          <Box className="match-success">
            <span className="success-icon">✓</span>
            <span>Properties appear to match!</span>
          </Box>
        )}

        <Box className="analysis-controls">
          <select value={dateRange} onChange={e => setDateRange(Number(e.target.value))} className="date-select">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>

          <Button className="btn btn-primary btn-analyze" onClick={fetchCombinedData} disabled={!selectedSite || !selectedProperty || loading}>
            {loading ? (
              <>
                <span className="btn-spinner"></span>Analyzing...
              </>
            ) : (
              "🔍 Run Combined Analysis"
            )}
          </Button>
        </Box>
      </Box>

      {error && (
        <Box className="error-card">
          <p>{error}</p>
          <Button className="btn btn-secondary" onClick={fetchCombinedData}>
            Retry
          </Button>
        </Box>
      )}

      {/* Results */}
      {analysisStarted && !loading && info && (
        <>
          <Box className="selected-summary">
            <Box className="summary-item gsc-summary">
              <span className="summary-label">GSC:</span>
              <span className="summary-value">{extractDomain(selectedSite)}</span>
            </Box>
            <span className="summary-connector">+</span>
            <Box className="summary-item ga-summary">
              <span className="summary-label">GA4:</span>
              <span className="summary-value">{gaProperties.find(p => p.propertyId === selectedProperty)?.displayName || selectedProperty}</span>
            </Box>
          </Box>

          {/* Tab Navigation */}
          <Box className="tab-navigation">
            <Button className={`tab-btn ${activeTab === "health" ? "active" : ""}`} onClick={() => setActiveTab("health")}>
              🏥 SEO Health
            </Button>
            <Button className={`tab-btn ${activeTab === "funnel" ? "active" : ""}`} onClick={() => setActiveTab("funnel")}>
              📊 Funnel
            </Button>
            <Button className={`tab-btn ${activeTab === "trends" ? "active" : ""}`} onClick={() => setActiveTab("trends")}>
              📈 Trends
            </Button>
            <Button className={`tab-btn ${activeTab === "devices" ? "active" : ""}`} onClick={() => setActiveTab("devices")}>
              📱 Devices
            </Button>
            <Button className={`tab-btn ${activeTab === "geo" ? "active" : ""}`} onClick={() => setActiveTab("geo")}>
              🌍 Geography
            </Button>
            <Button className={`tab-btn ${activeTab === "content" ? "active" : ""}`} onClick={() => setActiveTab("content")}>
              📝 Content Gaps
            </Button>
            <Button className={`tab-btn ${activeTab === "pages" ? "active" : ""}`} onClick={() => setActiveTab("pages")}>
              📄 Pages
            </Button>
            <Button className={`tab-btn ${activeTab === "keywords" ? "active" : ""}`} onClick={() => setActiveTab("keywords")}>
              🔑 Keywords
            </Button>
            <Button className={`tab-btn ${activeTab === "opportunities" ? "active" : ""}`} onClick={() => setActiveTab("opportunities")}>
              💡 Opportunities
            </Button>
          </Box>

          <Box className="tab-content">
            {activeTab === "health" && <SEOHealthView info={info?.seoHealth} />}
            {activeTab === "funnel" && <FunnelView info={info?.funnel} />}
            {activeTab === "trends" && <TrendCorrelationView info={info?.trendCorrelation} />}
            {activeTab === "devices" && <DeviceComparisonView info={info?.deviceComparison} />}
            {activeTab === "geo" && <GeoPerformanceView info={info?.geoPerformance} />}
            {activeTab === "content" && <ContentGapsView info={info?.contentGaps} />}
            {activeTab === "pages" && <LandingPagesView info={info?.landingPages} />}
            {activeTab === "keywords" && <KeywordsView info={info?.keywords} />}
            {activeTab === "opportunities" && <OpportunitiesView info={info?.opportunities} />}
          </Box>
        </>
      )}

      {!analysisStarted && !loading && (
        <Box className="initial-state">
          <Box className="initial-icon">🔗</Box>
          <h3>Ready to Combine Your Data</h3>
          <p>Select properties above and click on Run Combined Analysis to unlock powerful insights.</p>
          <Box className="benefits-list">
            <Box className="benefit-item">
              <span className="benefit-icon">🏥</span>
              <span>SEO Health Score</span>
            </Box>
            <Box className="benefit-item">
              <span className="benefit-icon">📊</span>
              <span>Conversion Funnel</span>
            </Box>
            <Box className="benefit-item">
              <span className="benefit-icon">📈</span>
              <span>Trend Correlation</span>
            </Box>
            <Box className="benefit-item">
              <span className="benefit-icon">📱</span>
              <span>Device Analysis</span>
            </Box>
            <Box className="benefit-item">
              <span className="benefit-icon">🌍</span>
              <span>Geographic Insights</span>
            </Box>
            <Box className="benefit-item">
              <span className="benefit-icon">📝</span>
              <span>Content Gap Analysis</span>
            </Box>
          </Box>
        </Box>
      )}
    </CombinedInsightsWrapper>
    // {/* </Box> */}
  );
}

// SEO Health Score View
function SEOHealthView({ info }) {
  if (!info) return <p>No health data available</p>;

  const getGradeColor = grade => {
    if (grade.startsWith("A")) return "#34a853";
    if (grade === "B") return "#4285f4";
    if (grade === "C") return "#fbbc04";
    return "#ea4335";
  };

  const getStatusIcon = status => {
    if (status === "good") return "✓";
    if (status === "warning") return "⚠";
    return "✗";
  };

  return (
    <Box className="seo-health-view">
      <h3>SEO Health Score</h3>

      <Box className="health-score-card">
        <Box className="score-circle" style={{ borderColor: getGradeColor(info.grade) }}>
          <span className="score-grade" style={{ color: getGradeColor(info.grade) }}>
            {info.grade}
          </span>
          <span className="score-number">{info.overallScore}/100</span>
        </Box>
        <Box className="score-comparison">
          <Box className="comparison-item">
            <span className="comparison-label">Clicks Change</span>
            <span className={`comparison-value ${info.comparison?.clicksChange >= 0 ? "positive" : "negative"}`}>
              {info.comparison?.clicksChange >= 0 ? "+" : ""}
              {info.comparison?.clicksChange}%
            </span>
          </Box>
          <Box className="comparison-item">
            <span className="comparison-label">Impressions Change</span>
            <span className={`comparison-value ${info.comparison?.impressionsChange >= 0 ? "positive" : "negative"}`}>
              {info.comparison?.impressionsChange >= 0 ? "+" : ""}
              {info.comparison?.impressionsChange}%
            </span>
          </Box>
        </Box>
      </Box>

      <Box className="health-metrics">
        <h4>Score Breakdown</h4>
        <Box className="metrics-list">
          {Object.entries(info.scores || {}).map(([key, metric]) => (
            <Box key={key} className={`metric-row status-${metric.status}`}>
              <Box className="metric-info">
                <span className="metric-icon">{getStatusIcon(metric.status)}</span>
                <span className="metric-name">{metric.label}</span>
              </Box>
              <Box className="metric-values">
                <span className="metric-actual">
                  {key === "ctr"
                    ? `${metric.value}%`
                    : key === "bounceRate"
                    ? `${metric.value}%`
                    : key === "sessionDuration"
                    ? `${Math.round(metric.value)}s`
                    : key === "trafficGrowth"
                    ? `${metric.value >= 0 ? "+" : ""}${metric.value.toFixed(1)}%`
                    : metric.value.toFixed(1)}
                </span>
                <Box className="score-bar">
                  <Box
                    className="score-fill"
                    style={{ width: `${metric.score}%`, backgroundColor: metric.status === "good" ? "#34a853" : metric.status === "warning" ? "#fbbc04" : "#ea4335" }}
                  ></Box>
                </Box>
                <span className="metric-score">{Math.round(metric.score)}</span>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {info.recommendations?.length > 0 && (
        <Box className="health-recommendations">
          <h4>Recommendations</h4>
          {info.recommendations.map((rec, index) => (
            <Box className={`recommendation-item priority-${rec.priority}`}>
              <span className="rec-area">{rec.area}</span>
              <span className="rec-message">{rec.message}</span>
              <span className={`rec-priority ${rec.priority}`}>{rec.priority}</span>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// Trend Correlation View
function TrendCorrelationView({ info }) {
  if (!info) return <p>No trend data available</p>;

  return (
    <Box className="trend-correlation-view">
      <h3>Trend Correlation Analysis</h3>
      <br />
      <Box className="correlation-summary">
        <Box className="correlation-card">
          <Box className="correlation-label">
            Clicks ↔ Sessions Correlation: {info.correlation?.clicksToSessions} - {info.correlation?.interpretation}
          </Box>
          {/* <span className={`correlation-value ${info.correlation?.interpretation}`}>{info.correlation?.clicksToSessions}</span>
          <span className="correlation-interpretation">{info.correlation?.interpretation}</span> */}
        </Box>
        <Box className="summary-stats">
          <Box className="stat-item">
            <Box className="stat-label">Avg Daily Clicks: {info.summary?.avgDailyClicks}</Box>
            {/* <span className="stat-value">{info.summary?.avgDailyClicks}</span> */}
          </Box>
          <Box className="stat-item">
            <Box className="stat-label">Avg Daily Sessions: {info.summary?.avgDailySessions}</Box>
            {/* <span className="stat-value">{info.summary?.avgDailySessions}</span> */}
          </Box>
        </Box>
      </Box>

      <Box className="trend-chart">
        <h4>Daily Trends (GSC vs GA4)</h4>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={info.trends || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Line yAxisId="left" type="monotone" dataKey="clicks" stroke="#4285f4" name="Clicks (GSC)" strokeWidth={2} />
            <Line yAxisId="right" type="monotone" dataKey="sessions" stroke="#34a853" name="Sessions (GA4)" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {info.anomalies?.length > 0 && (
        <Box className="anomalies-section">
          <h4>⚠️ Anomalies Detected</h4>
          <Box className="anomalies-list">
            {info.anomalies.map((anomaly, index) => (
              <Box className={`anomaly-item ${anomaly.type}`}>
                <Box className="anomaly-date">
                  {anomaly.date}: {anomaly.message}
                </Box>
                {/* <Box className="anomaly-message">{anomaly.message}</Box> */}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// Device Comparison View
function DeviceComparisonView({ info }) {
  if (!info) return <p>No device data available</p>;

  return (
    <Box className="device-comparison-view">
      <h3>Device Performance Comparison</h3>

      {info.insights?.length > 0 && (
        <Box className="device-insights">
          {info.insights.map((insight, index) => (
            <Box className={`insight-card ${insight.type} impact-${insight.impact}`}>
              <span className="insight-title">{insight.title}</span>
              <span className="insight-message">{insight.message}</span>
            </Box>
          ))}
        </Box>
      )}

      <Box className="device-chart">
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={info.devices || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="device" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Bar yAxisId="left" dataKey="clicks" fill="#4285f4" name="Clicks (GSC)" />
            <Bar yAxisId="right" dataKey="sessions" fill="#34a853" name="Sessions (GA4)" />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Box className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Device</th>
              <th className="gsc-col">Clicks</th>
              <th className="gsc-col">Impressions</th>
              <th className="gsc-col">CTR</th>
              <th className="gsc-col">Position</th>
              <th className="ga-col">Sessions</th>
              <th className="ga-col">Users</th>
              <th>Efficiency</th>
            </tr>
          </thead>
          <tbody>
            {(info.devices || []).map((device, index) => (
              <tr>
                <td>
                  <strong>{device.device}</strong>
                </td>
                <td className="gsc-col">{device.clicks?.toLocaleString()}</td>
                <td className="gsc-col">{device.impressions?.toLocaleString()}</td>
                <td className="gsc-col">{device.ctr}%</td>
                <td className="gsc-col">{device.position}</td>
                <td className="ga-col">{device.sessions?.toLocaleString()}</td>
                <td className="ga-col">{device.users?.toLocaleString()}</td>
                <td>{device.efficiency}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}

// Geographic Performance View
function GeoPerformanceView({ info }) {
  if (!info) return <p>No geographic data available</p>;

  return (
    <Box className="geo-performance-view">
      <h3>Geographic Performance</h3>
      <br />
      <Box className="geo-summary">
        <Box className="summary-stat">
          <Box className="stat-value">Countries: {info.summary?.totalCountries}</Box>
          {/* <span className="stat-label">Countries</span> */}
        </Box>
        <Box className="summary-stat">
          <Box className="stat-value">Top Country: {info.summary?.topCountry}</Box>
          {/* <span className="stat-label">Top Country</span> */}
        </Box>
        <Box className="summary-stat">
          <Box className="stat-value">Total Clicks: {info.summary?.totalClicks?.toLocaleString()}</Box>
          {/* <span className="stat-label">Total Clicks</span> */}
        </Box>
      </Box>

      <Box className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Country</th>
              <th className="gsc-col">Clicks</th>
              <th className="gsc-col">Impressions</th>
              <th className="gsc-col">CTR</th>
              <th className="gsc-col">Position</th>
              <th className="ga-col">Sessions</th>
              <th>Efficiency</th>
              <th>Opportunity</th>
            </tr>
          </thead>
          <tbody>
            {(info.countries || []).map((country, index) => (
              <tr>
                <td>
                  <strong>{country.country}</strong>
                </td>
                <td className="gsc-col">{country.clicks?.toLocaleString()}</td>
                <td className="gsc-col">{country.impressions?.toLocaleString()}</td>
                <td className="gsc-col">{country.ctr}%</td>
                <td className="gsc-col">{country.position}</td>
                <td className="ga-col">{country.sessions?.toLocaleString()}</td>
                <td>{country.efficiency}%</td>
                <td>
                  <span className={`opportunity-badge ${country.opportunity?.toLowerCase().replace(" ", "-")}`}>{country.opportunity}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      {(info.opportunities?.highPotential?.length > 0 || info.opportunities?.lowEngagement?.length > 0) && (
        <Box className="geo-opportunities">
          {info.opportunities?.highPotential?.length > 0 && (
            <Box className="opportunity-section">
              <h4>🎯 High Potential Markets</h4>
              <p className="section-desc">High impressions but low CTR - improve visibility</p>
              {info.opportunities.highPotential.map((c, i) => (
                <Box className="opportunity-item">
                  <Box className="country-name">
                    {c.country}: {c.impressions?.toLocaleString()} impressions, {c.ctr}% CTR
                  </Box>
                  {/* <span className="country-stats">
                    {c.impressions?.toLocaleString()} impressions, {c.ctr}% CTR
                  </span> */}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// Content Gaps View
function ContentGapsView({ info }) {
  if (!info) return <p>No content gap data available</p>;

  return (
    <Box className="content-gaps-view">
      <h3>Content Gap Analysis</h3>

      <Box className="gaps-summary">
        <Box className="summary-stat">
          <Box className="stat-values">Keywords Analyzed: {info.summary?.totalKeywords}</Box>
          {/* <span className="stat-label">Keywords Analyzed</span> */}
        </Box>
        <Box className="summary-stat">
          <Box className="stat-values">Hidden Gems: {info.summary?.hiddenGemsCount}</Box>
          {/* <span className="stat-label">Hidden Gems</span> */}
        </Box>
        <Box className="summary-stat">
          <Box className="stat-values">Quick Wins: {info.summary?.quickWinsCount}</Box>
          {/* <span className="stat-label">Quick Wins</span> */}
        </Box>
      </Box>

      {info.quickWins?.length > 0 && (
        <Box className="content-section">
          <h4>🚀 Quick Wins (Position 11-20)</h4>
          <p className="section-desc">Keywords close to page 1 - small improvements can drive big results</p>
          <Box className="quick-wins-list">
            {info.quickWins.map((kw, index) => (
              <Box className="quick-win-item">
                <Box className="kw-info">
                  <Box className="kw-name">
                    {kw.keyword}: {kw.recommendation}
                  </Box>
                  {/* <Box className="kw-rec">{kw.recommendation}</Box> */}
                </Box>
                <Box className="kw-stats">
                  <Box>
                    Position: <strong>{kw.position}</strong>
                  </Box>
                  <Box>
                    Impressions: <strong>{kw.impressions?.toLocaleString()}</strong>
                  </Box>
                  <Box>
                    Clicks: <strong>{kw.clicks}</strong>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {info.hiddenGems?.length > 0 && (
        <Box className="content-section">
          <h4>💎 Hidden Gems</h4>
          <p className="section-desc">Pages with high engagement but low search visibility - optimize for SEO</p>
          <Box className="hidden-gems-list">
            {info.hiddenGems.map((page, index) => (
              <Box className="hidden-gem-item">
                <span className="gem-page">{page.page}</span>
                <Box className="gem-stats">
                  <span>{page.pageViews?.toLocaleString()} page views</span>
                  <span>{page.users?.toLocaleString()} users</span>
                </Box>
                <span className="gem-rec">{page.recommendation}</span>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {info.underservedTopics?.length > 0 && (
        <Box className="content-section">
          <h4>📚 Underserved Topics</h4>
          <p className="section-desc">Topic clusters with high search demand but poor rankings</p>
          <Box className="topics-list">
            {info.underservedTopics.map((topic, index) => (
              <Box className="topic-item">
                <Box className="topic-header">
                  <span className="topic-name">{topic.topic}</span>
                  <span className="topic-count">{topic.keywordCount} keywords</span>
                </Box>
                <Box className="topic-stats">
                  <span>{topic.totalImpressions?.toLocaleString()} impressions</span>
                  <span>Avg position: {topic.avgPosition}</span>
                </Box>
                <Box className="topic-keywords">{topic.topKeywords?.join(", ")}</Box>
                <span className="topic-rec">{topic.recommendation}</span>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// Funnel View
function FunnelView({ info }) {
  if (!info) return <p>No funnel data available</p>;

  return (
    <Box className="funnel-view">
      <h3>Search-to-Conversion Funnel</h3>
      <Box className="funnel-stages">
        {info.stages?.map((stage, index) => (
          <Box key={stage.name} className="funnel-stage">
            <Box className="stage-header">
              <span className="stage-name">{stage.name}</span>
              <span className="stage-source">{stage.source}</span>
            </Box>
            <Box className="stage-value">{stage.value.toLocaleString()}</Box>
            <Box className="stage-description">{stage.description}</Box>
            {stage.conversionRate !== undefined && <Box className="stage-conversion">↓ {stage.conversionRate}% conversion</Box>}
          </Box>
        ))}
      </Box>
      <Box className="overall-metrics">
        <h4>Conversion Rates</h4>
        <Box className="metrics-grid">
          <Box className="metric-card">
            <span className="metric-label">Search → Click</span>
            <span className="metric-value">{info.overallMetrics?.searchToClickRate}%</span>
          </Box>
          <Box className="metric-card">
            <span className="metric-label">Click → Session</span>
            <span className="metric-value">{info.overallMetrics?.clickToSessionRate}%</span>
          </Box>
          <Box className="metric-card">
            <span className="metric-label">Session → Conversion</span>
            <span className="metric-value">{info.overallMetrics?.sessionToConversionRate}%</span>
          </Box>
          <Box className="metric-card highlight">
            <span className="metric-label">Search → Conversion</span>
            <span className="metric-value">{info.overallMetrics?.searchToConversionRate}%</span>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// Landing Pages View
function LandingPagesView({ info }) {
  if (!info || info.length === 0) return <p>No landing page data available</p>;

  return (
    <Box className="landing-pages-view">
      <h3>Landing Page Performance</h3>
      <Box className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Page</th>
              <th className="gsc-col">Clicks</th>
              <th className="gsc-col">Impressions</th>
              <th className="gsc-col">CTR</th>
              <th className="gsc-col">Position</th>
              <th className="ga-col">Page Views</th>
              <th className="ga-col">Bounce Rate</th>
              <th>Opportunity</th>
            </tr>
          </thead>
          <tbody>
            {info.map((page, index) => (
              <tr>
                <td className="page-cell" title={page.page}>
                  {page.path || page.page}
                </td>
                <td className="gsc-col">{page.clicks.toLocaleString()}</td>
                <td className="gsc-col">{page.impressions.toLocaleString()}</td>
                <td className="gsc-col">{page.ctr}%</td>
                <td className="gsc-col">{page.position}</td>
                <td className="ga-col">{page.pageViews.toLocaleString()}</td>
                <td className="ga-col">{page.bounceRate}%</td>
                <td>
                  <span className={`opportunity-badge ${page.opportunity?.toLowerCase().replace(" ", "-")}`}>{page.opportunity}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      {/* <Box className="table-legend">
        <span className="legend-item">GSC Metrics (Search)</span>
        <span className="legend-item">GA4 Metrics (Engagement)</span>
      </Box> */}
    </Box>
  );
}

// Keywords View
function KeywordsView({ info }) {
  if (!info) return <p>No keyword data available</p>;
  const keywords = info.keywords || [];

  return (
    <Box className="keywords-view">
      <h3>Keyword Performance</h3>
      <br />
      <Box className="keyword-summary">
        <Box className="summary-stat">
          <Box className="stat-value">Total Clicks: {info.totalClicks?.toLocaleString()}</Box>
          {/* <span className="stat-label">Total Clicks</span> */}
        </Box>
        <Box className="summary-stat">
          <Box className="stat-value">Total Conversions: {info.totalConversions?.toLocaleString()}</Box>
          {/* <span className="stat-label">Total Conversions</span> */}
        </Box>
        <Box className="summary-stat">
          <Box className="stat-value">Keywords Tracked: {keywords.length}</Box>
          {/* <span className="stat-label">Keywords Tracked</span> */}
        </Box>
      </Box>
      <Box className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Keyword</th>
              <th>Clicks</th>
              <th>Impressions</th>
              <th>CTR</th>
              <th>Position</th>
              <th>Click Share</th>
              <th>Est. Sessions</th>
              <th>Est. Conversions</th>
              <th>Potential</th>
            </tr>
          </thead>
          <tbody>
            {keywords.map((kw, index) => (
              <tr>
                <td className="keyword-cell">{kw.keyword}</td>
                <td>{kw.clicks.toLocaleString()}</td>
                <td>{kw.impressions.toLocaleString()}</td>
                <td>{kw.ctr}%</td>
                <td>{kw.position}</td>
                <td>{kw.clickShare}%</td>
                <td>{kw.estimatedSessions}</td>
                <td>{kw.estimatedConversions}</td>
                <td>
                  <span className={`potential-badge ${kw.potential?.toLowerCase()}`}>{kw.potential}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}

// Opportunities View
function OpportunitiesView({ info }) {
  if (!info) return <p>No opportunity data available</p>;

  const categories = [
    { key: "improveTitles", title: "📝 Improve Titles & Descriptions", description: "High impressions but low CTR" },
    { key: "improveContent", title: "📄 Improve Content Relevance", description: "High CTR but high bounce rate" },
    { key: "improveSEO", title: "🔍 Improve SEO", description: "Good engagement but low rankings" },
    { key: "featuredSnippet", title: "⭐ Featured Snippet Opportunities", description: "Already ranking well" },
    { key: "topPerformers", title: "🏆 Top Performers", description: "Your best performing pages" },
  ];

  return (
    <Box className="opportunities-view">
      <h3>Content Optimization Opportunities</h3>
      {categories.map(category => {
        const items = info[category.key] || [];
        if (items.length === 0) return null;
        return (
          <Box className="opportunity-category">
            <Box className="category-header">
              <h4>{category.title}</h4>
              <p>{category.description}</p>
              <span className="item-count">pages: {items.length}</span>
            </Box>
            <Box className="opportunity-items">
              {items.slice(0, 5).map((item, index) => (
                <Box className={`opportunity-item priority-${item.priority}`}>
                  <Box className="item-page" title={item.page}>
                    {item.path || item.page}
                  </Box>
                  <Box className="item-metrics">
                    <Box>Clicks: {item.clicks}</Box>
                    <Box>Impressions: {item.impressions}</Box>
                    <Box>CTR: {item.ctr}%</Box>
                    <Box>Position: {item.position}</Box>
                  </Box>
                  <Box className="item-recommendation">💡 {item.recommendation}</Box>
                  <span className={`priority-badge ${item.priority}`}>{item.priority} priority</span>
                </Box>
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default CombinedInsights;
